import { ShieldOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/fleet/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRevokeUserSessions } from '@/hooks/use-revoke-user-sessions';
import { ApiError } from '@/lib/api-client';

/**
 * Restricted operator action — bulk-revoke EVERY proxy session belonging
 * to a single user. First-response tool for confirmed TOS abuse (spam,
 * scraping, etc.); see backend `proxy-session.controller.ts`. The user's
 * `cache:proxy:token:*` + `cache:vless-session:*` entries are dropped
 * within ~1s and their next request gets 407/401.
 *
 * Wrapped in a type-to-confirm dialog because there is no undo — the
 * user will need to reconnect to get a fresh session.
 */
export function RevokeUserSessionsCard(): JSX.Element {
  const [userIdInput, setUserIdInput] = useState('');
  const [parsedUserId, setParsedUserId] = useState<number | null>(null);
  const revoke = useRevokeUserSessions();

  function handleOpen(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = userIdInput.trim();
    const parsed = Number(trimmed);
    if (!trimmed || !Number.isInteger(parsed) || parsed <= 0) {
      toast.error('Enter a positive integer user id.');
      return;
    }
    setParsedUserId(parsed);
  }

  function handleConfirm(): void {
    if (parsedUserId === null) return;
    revoke.mutate(parsedUserId, {
      onSuccess: (result) => {
        toast.success(
          result.revoked > 0
            ? `Revoked ${result.revoked} session${result.revoked === 1 ? '' : 's'} for user ${parsedUserId}.`
            : `User ${parsedUserId} had no active sessions.`,
        );
        setParsedUserId(null);
        setUserIdInput('');
      },
      onError: (err) => {
        toast.error(err instanceof ApiError ? err.message : 'Revoke failed.');
        setParsedUserId(null);
      },
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldOff className="h-4 w-4 text-destructive" aria-hidden />
          Revoke user sessions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          Drops every active proxy session for a user (TOS-abuse first-response). The user's
          next request returns 407/401; they can reconnect to get a fresh session.
        </p>
        <form onSubmit={handleOpen} className="flex items-end gap-3" autoComplete="off">
          <div className="flex-1 space-y-1">
            <Label htmlFor="revoke-user-id">User id</Label>
            <Input
              id="revoke-user-id"
              inputMode="numeric"
              pattern="[0-9]*"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              placeholder="e.g. 4271"
            />
          </div>
          <Button type="submit" variant="destructive" disabled={revoke.isPending}>
            Revoke sessions
          </Button>
        </form>
      </CardContent>

      <ConfirmDialog
        open={parsedUserId !== null}
        onOpenChange={(next) => { if (!next) setParsedUserId(null); }}
        title="Revoke all sessions"
        description={
          <span>
            Drop every active proxy session for user{' '}
            <span className="font-mono font-semibold">{parsedUserId}</span>?
            This cannot be undone — the user must reconnect.
          </span>
        }
        variant="destructive"
        confirmLabel="Revoke sessions"
        confirmPhrase="REVOKE"
        pending={revoke.isPending}
        onConfirm={handleConfirm}
      />
    </Card>
  );
}
